// src/hooks/useInnovaTechGating.js
// 🔐 Sistema de gating pedagógico para InnovaTech
// Valida completion_criteria y prerequisites por step/lab

import { useState, useEffect, useMemo } from 'react';
import coachLogic from '../services/innovatech/coachLogic_modo_innovatech.json';

/**
 * Hook para manejar las reglas de gating pedagógico de InnovaTech
 * @param {string} labId - ID del laboratorio actual 
 * @param {number} currentStep - Índice del step actual
 * @param {Object} sessionData - Datos de la sesión del usuario
 * @returns {Object} Estado de gating y funciones de validación
 */
export const useInnovaTechGating = (labId, currentStep, sessionData = null) => {
  const [completionStatus, setCompletionStatus] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  
  // 🛡️ DATOS SEGUROS - Manejo de sessionData null/undefined
  const safeSessionData = useMemo(() => ({
    completedSteps: {},
    completedLabs: [],
    labScores: {},
    evidences: {},
    ...sessionData
  }), [sessionData]);
  
  // Obtener configuración del lab actual
  const currentLab = useMemo(() => {
    return coachLogic.labs.find(lab => lab.lab_id === labId);
  }, [labId]);

  // Obtener step actual
  const currentStepData = useMemo(() => {
    if (!currentLab || currentStep < 0 || currentStep >= currentLab.steps.length) {
      return null;
    }
    return currentLab.steps[currentStep];
  }, [currentLab, currentStep]);

  /**
   * Valida si un step está completo según completion_criteria
   */
  const validateStepCompletion = (stepData, userInputs = {}) => {
    if (!stepData?.completion_criteria) {
      return { isComplete: true, errors: [] };
    }

    const criteria = stepData.completion_criteria;
    const errors = [];
    let isComplete = true;

    // Validar criterios por tipo
    if (criteria.on_option) {
      // Requiere selección de opción específica
      const selectedOption = userInputs.selectedOption;
      if (!selectedOption || !criteria.on_option.includes(selectedOption)) {
        isComplete = false;
        errors.push('Debes seleccionar una opción válida para continuar');
      }
    }

    if (criteria.on_save) {
      // Requiere guardar input
      const inputText = userInputs.inputText || '';
      if (!inputText.trim()) {
        isComplete = false;
        errors.push('Debes completar el campo de texto');
      }

      // Validar criterios específicos de input
      if (stepData.validation) {
        const validation = stepData.validation;
        
        if (validation.min_chars && inputText.length < validation.min_chars) {
          isComplete = false;
          errors.push(`Mínimo ${validation.min_chars} caracteres requeridos`);
        }

        if (validation.must_include) {
          validation.must_include.forEach(required => {
            if (!inputText.includes(required)) {
              isComplete = false;
              errors.push(`Debe incluir: "${required}"`);
            }
          });
        }
      }
    }

    if (criteria.min_score) {
      // Para quiz - requiere score mínimo
      const score = userInputs.quizScore || 0;
      if (score < criteria.min_score) {
        isComplete = false;
        errors.push(`Score mínimo requerido: ${criteria.min_score}%`);
      }
    }

    if (criteria.required_elements) {
      // Para elementos compuestos (ej: BML circuit)
      const elements = userInputs.elements || {};
      criteria.required_elements.forEach(element => {
        if (!elements[element]) {
          isComplete = false;
          errors.push(`Elemento requerido: ${element}`);
        }
      });
    }

    return { isComplete, errors };
  };

  /**
   * Valida prerequisites para acceder a un lab
   */
  const validateLabPrerequisites = (targetLabId) => {
    const targetLab = coachLogic.labs.find(lab => lab.lab_id === targetLabId);
    if (!targetLab?.prerequisites) {
      return { canAccess: true, errors: [] };
    }

    const errors = [];
    let canAccess = true;

    targetLab.prerequisites.forEach(prerequisite => {
      const [type, requirement] = prerequisite.split(':');
      
      switch (type) {
        case 'lab_completed':
          const completedLabs = safeSessionData.completedLabs;
          if (!completedLabs.includes(requirement)) {
            canAccess = false;
            errors.push(`Debes completar ${requirement} primero`);
          }
          break;
          
        case 'min_score':
          const labScores = safeSessionData.labScores;
          const score = labScores[requirement] || 0;
          if (score < 70) {
            canAccess = false; 
            errors.push(`Score mínimo 70% requerido en ${requirement}`);
          }
          break;
          
        case 'evidence_uploaded':
          const evidences = safeSessionData.evidences;
          if (!evidences[requirement]) {
            canAccess = false;
            errors.push(`Evidencia requerida: ${requirement}`);
          }
          break;
      }
    });

    return { canAccess, errors };
  };

  /**
   * Verifica si se puede avanzar al siguiente step
   */
  const canAdvanceToNextStep = (userInputs = {}) => {
    if (!currentStepData) {
      return { canAdvance: false, errors: ['Step no válido'] };
    }

    console.log('[🔍 Gating] Validando step:', currentStepData.step_id);
    console.log('[🔍 Gating] UserInputs recibidos:', userInputs);
    console.log('[🔍 Gating] Completion criteria:', currentStepData.completion_criteria);

    const validation = validateStepCompletion(currentStepData, userInputs);
    return {
      canAdvance: validation.isComplete,
      errors: validation.errors
    };
  };

  /**
   * Verifica si se puede avanzar al siguiente lab
   */
  const canAdvanceToNextLab = () => {
    if (!currentLab) {
      return { canAdvance: false, errors: ['Lab no válido'] };
    }

    // Verificar que todos los steps obligatorios estén completos
    const requiredSteps = currentLab.steps.filter(step => 
      step.completion_criteria && !step.optional
    );

    const completedSteps = safeSessionData.completedSteps;
    const labCompletedSteps = completedSteps[labId] || [];

    const missingSteps = requiredSteps.filter(step => 
      !labCompletedSteps.includes(step.step_id)
    );

    if (missingSteps.length > 0) {
      return {
        canAdvance: false,
        errors: [`Completa los steps: ${missingSteps.map(s => s.name).join(', ')}`]
      };
    }

    return { canAdvance: true, errors: [] };
  };

  /**
   * Calcula el progreso del lab actual
   */
  const calculateLabProgress = () => {
    if (!currentLab) return 0;

    const totalSteps = currentLab.steps.filter(step => !step.optional).length;
    const completedSteps = safeSessionData.completedSteps;
    const labCompletedSteps = completedSteps[labId] || [];
    
    return Math.round((labCompletedSteps.length / totalSteps) * 100);
  };

  /**
   * Actualiza el estado de completitud de un step
   */
  const markStepCompleted = (stepId, userInputs = {}) => {
    const stepData = currentLab?.steps.find(step => step.step_id === stepId);
    if (!stepData) return false;

    const validation = validateStepCompletion(stepData, userInputs);
    
    if (validation.isComplete) {
      setCompletionStatus(prev => ({
        ...prev,
        [stepId]: true
      }));
      
      // Guardar en sessionData (esto se sincronizaría con backend)
      const updatedCompletedSteps = {
        ...safeSessionData.completedSteps,
        [labId]: [...(safeSessionData.completedSteps[labId] || []), stepId]
      };
      
      // TODO: Sync con backend
      console.log('[🔐 Gating] Step completado:', stepId, userInputs);
      
      return true;
    } else {
      setValidationErrors(prev => ({
        ...prev,
        [stepId]: validation.errors
      }));
      return false;
    }
  };

  return {
    // Estado
    currentLab,
    currentStepData,
    completionStatus,
    validationErrors,
    
    // Funciones de validación
    validateStepCompletion,
    validateLabPrerequisites,
    canAdvanceToNextStep,
    canAdvanceToNextLab,
    calculateLabProgress,
    markStepCompleted,
    
    // Utilidades
    isStepCompleted: (stepId) => !!completionStatus[stepId],
    getStepErrors: (stepId) => validationErrors[stepId] || [],
    labProgress: calculateLabProgress()
  };
};

export default useInnovaTechGating;